import React, {useState, useEffect} from 'react';

import "./RootCauseOverview.css";
import {StandardButton} from "../../../../common/button/standard-button/StandardButton";
import {BreadCrumbTrail} from "../../../../common/breadcrumb-trail/BreadcrumbTrail";
import problemIcon from "../../../../../resources/images/problem-icon.png";
import {OverviewListItem} from "../../../../common/list/overview-list-item/OverviewListItem";
import {deleteCause, viewCause, viewCausesForProblem} from "../../../../../services/CauseService";
import {ToolTip} from "../../../../common/tool-tip/ToolTip";
import {ConfirmDeleteModal} from "../../../../common/confirmation-modal/ConfimDeleteModal";

export const RootCauseOverview = ({context, setContext, navigateTo, setSpinnerText, showToaster, toolTipsEnabled}) => {

    const [rootCauseList, setRootCauseList] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState(null);
    const [extRefToBeDeleted, setExtRefToBeDeleted] = useState(null);

    useEffect(() => {
        if (!context.viewMode) {
            setContext((prevState) => ({
                ...prevState,
                breadcrumbs: {
                    ...prevState.breadcrumbs,
                    rootCauseBreadcrumb: {
                        ...prevState.breadcrumbs.rootCauseBreadcrumb,
                        text: "Overview",
                        type: "Root Cause",
                        screenKey: "rootCauseOverview",
                    },
                },
            }));
        }

        const fetchData = async () => {
            setSpinnerText("Retrieving Causes");
            await retrieveCauses();
            setSpinnerText(null);
        }
        fetchData();

    }, []);

    const retrieveCauses = async () => {
        const [data, error] = await viewCausesForProblem(context.username, context.problemScreen.externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const mappedRootCauseList = data.causeVOS.map((rootCause) => {
                    return {
                        externalReference: rootCause.externalReference,
                        name: rootCause.name,
                        description: rootCause.description,
                        fileList: rootCause.causeEvidenceList,
                        priority: {label: rootCause.priority, value: rootCause.priority},
                    }
                });
                setRootCauseList(mappedRootCauseList);
            } else {
                showToaster(data);
            }
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
    }

    const handleAddRootCauseClick = () => {
        setContext((prevState) => ({
            ...prevState,
            rootCauseScreen: null,
            breadcrumbs: {
                ...prevState.breadcrumbs,
                changeIdeaBreadcrumb: null,
                testBreadCrumb: null,
            },
        }));
        navigateTo("rootCauseOne");
    }

    const fetchRootCause = async (rootCauseExternalReference) => {
        setSpinnerText("Retrieving Cause");
        const [data, error] = await viewCause(context.username, rootCauseExternalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                const {causeVOToView} = data;
                setSpinnerText(null);
                return {
                    externalReference: causeVOToView.externalReference,
                    name: causeVOToView.name,
                    description: causeVOToView.description,
                    fileList: causeVOToView.causeEvidenceList,
                    priority: {label: causeVOToView.priority, value: causeVOToView.priority},
                }
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }
        setSpinnerText(null);
    }

    const handleListItemClick = async (rootCauseExternalReference) => {
        const savedRootCause = await fetchRootCause(rootCauseExternalReference);
        setContext((prevState) => ({
            ...prevState,
            rootCauseScreen: {...savedRootCause},
            breadcrumbs: {
                ...prevState.breadcrumbs,
                PDSABreadCrumb: null,
            },
        }));
        navigateTo("changeIdeaOverview");
    }

    const handleViewListItemClick = async (rootCauseExternalReference) => {
        const savedRootCause = await fetchRootCause(rootCauseExternalReference);
        setContext((prevState) => ({
            ...prevState,
            copyOfRootCauseScreen: null,
            rootCauseScreen: {...savedRootCause}
        }));
        navigateTo("rootCauseOne");
    }

    const handleDeleteListItemClick = async (externalReference) => {
        setSpinnerText("Deleting Root Cause");
        let rootCauseDeleted = false;
        const [data, error] = await deleteCause(context.username, externalReference);
        if (data) {
            if (data.responseStatus === "Success") {
                rootCauseDeleted = true;
            }
            showToaster(data);
        } else {
            showToaster({
                responseStatus: "Error",
                responseMessage: error.message,
            });
        }

        if (rootCauseDeleted) {
            await retrieveCauses();
            setShowModal(false);
        }
        setSpinnerText(null);
    }

    const buildRootCauseList = () => {
        if (rootCauseList) {
            return rootCauseList.map((rootCause, index) => {
                return (
                    <OverviewListItem key={index}
                                      text={rootCause.name}
                                      disableDelete={!context.editMode}
                                      buttonThreeText={"CI"}
                                      onItemClick={() => {
                                          handleListItemClick(rootCause.externalReference);
                                      }}
                                      onViewClick={() => {
                                          handleViewListItemClick(rootCause.externalReference);
                                      }}
                                      onDeleteClick={() => {
                                          handleDeleteModalShow(rootCause.externalReference, rootCause.name);
                                      }}/>
                );
            });
        }
    }

    const handleDeleteModalShow = async (extRef, title) => {
        setShowModal(true);
        setExtRefToBeDeleted(extRef);
        setModalTitle(title);
    }

    return (
        <div className="wizard-content-container">
            <div className="wizard-main-content-container">
                <div className="wizard-left-container">
                    <div className="wizard-icon-container">
                        <img src={problemIcon} alt="Aurum Logo" className="wizard-icon"/>
                    </div>
                    <p className="wizard-icon-label">{context.problemScreen.title}</p>
                </div>
                <div className="wizard-right-container">
                    <div className="root-cause-overview-list-container">
                        <div className={"label-div-tooltip-h2"}>
                            <label className={"label-h2"}>Root Cause Overview</label>
                            {toolTipsEnabled &&
                            <ToolTip tooltipExternalReference={"rootcauseOverview123"}
                                     context={context}
                                     setSpinnerText={setSpinnerText} showToaster={showToaster}
                                     fieldName={"Root Cause Overview:"}/>}
                        </div>
                        {buildRootCauseList()}
                        <StandardButton text="Add Root Cause" onClick={handleAddRootCauseClick}
                                        disabled={!context.editMode}/>
                    </div>
                    {context.viewMode && <div className="back-to-overview-button-container">
                        <StandardButton text="Problem Overview" onClick={() => {
                            navigateTo("problemTree");
                        }}/>
                    </div>}
                </div>
            </div>
            {showModal &&
            <ConfirmDeleteModal showModal={showModal}
                                setShowModal={setShowModal}
                                titleToDelete={modalTitle}
                                onDeleteClick={() => {
                                    handleDeleteListItemClick(extRefToBeDeleted)
                                }}/>}
            <div className="wizard-breadcrumb-container">
                <BreadCrumbTrail context={context} navigateTo={navigateTo}/>
            </div>
        </div>
    );
}
